import { EnumerationDto } from '@/api/backoffice/clients';
import { ENUM_DeliveryStatusTypes, ENUM_ProductStatusTypes } from '@/modules/core/common';
import { i18n } from '@/i18n';
import { PublicationVersion, PublicationVersionSearchResult } from '@/api/frontoffice/clients';

export interface IPublicationVersionState {
    isInError: boolean;
    description: string;
}

export class PublicationVersionState implements IPublicationVersionState {
    version: PublicationVersionSearchResult | PublicationVersion;

    private deliveryErrorStates: EnumerationDto[] = [
        ENUM_DeliveryStatusTypes.Gefaald,
        ENUM_DeliveryStatusTypes.Afgebroken,
        ENUM_DeliveryStatusTypes.Geweigerd,
        ENUM_DeliveryStatusTypes.GeweigerdDoorEvaluator,
        ENUM_DeliveryStatusTypes.GeweigerdDoorEvaluator2
    ];

    private productErrorStates: EnumerationDto[] = [
        ENUM_ProductStatusTypes.Gefaald,
        ENUM_ProductStatusTypes.Afgebroken
    ];

    constructor(version: PublicationVersionSearchResult | PublicationVersion) {
        this.version = version;
    }

    private get hasDeliveryFailed(): boolean {
        const status = this.version.deliveryStatusName;
        if (status) {
            return this.deliveryErrorStates.some((s) => s.name.toLowerCase() === status.toLowerCase());
        }
    }

    private get hasProductFailed(): boolean {
        const status = this.version.productStatusName;
        if (status) {
            return this.productErrorStates.some((s) => s.name.toLowerCase() === status.toLowerCase());
        }
    }

    private get isProcessed(): boolean {
        const processedStates = [ENUM_ProductStatusTypes.Verwerkt];

        const productStatus = this.version.productStatusName;
        if (productStatus) {
            return processedStates.some((s) => s.name.toLowerCase() === productStatus.toLowerCase());
        }
        return false;
    }

    private get isProcessing(): boolean {
        if (this.isProcessed) {
            return false;
        }

        if (this.isInError) {
            return false;
        }

        return true;
    }

    get isInError(): boolean {
        if (this.isProcessed) {
            return false;
        }

        return this.hasDeliveryFailed || this.hasProductFailed;
    }

    get description(): string {
        let label;
        if (this.isProcessing) {
            label = i18n.global.t('publications.states.version.processing');
        } else if (this.isProcessed) {
            label = i18n.global.t('publications.states.version.published');
        } else {
            const productStatus = this.version.productStatusName;
            const deliveryStatus = this.version.deliveryStatusName;
            if (productStatus) {
                label = productStatus;
            } else if (deliveryStatus) {
                label = deliveryStatus;
            }
        }
        return label;
    }
}
